import type {
  Paginated,
  PaginatedResponse,
  PaginationParams,
  QueryArgs,
  Sortable,
  StructureDocumentType,
} from "@ac-expo/api-types";
import { isNullish } from "@ac-expo/utils";
import {
  InfiniteData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { structureDocumentsQueryKeys } from "../query-keys/structure-documents.query-keys";

export type FilterStructureDocumentType = {
  searchText?: string;
};

const getStructureDocuments = async (
  structureId: string,
  params: Sortable<Paginated<FilterStructureDocumentType>>,
) => {
  const res = await privateAxios.get<PaginatedResponse<StructureDocumentType>>(
    `/structures/${structureId}/documents`,
    { params },
  );
  return res.data;
};

export const useGetStructureDocumentsQuery = (
  structureId: string,
  params: Sortable<Paginated<FilterStructureDocumentType>>,
  options?: QueryArgs<PaginatedResponse<StructureDocumentType>>,
) => {
  return useQuery({
    queryKey: structureDocumentsQueryKeys.list(structureId, params),
    queryFn: () => getStructureDocuments(structureId, params),
    enabled: structureId !== "",
    ...options,
  });
};

export const useInfiniteStructureDocumentsQuery = (
  structureId: string,
  filter?: Sortable<FilterStructureDocumentType>,
  options?: Parameters<
    typeof useInfiniteQuery<
      PaginatedResponse<StructureDocumentType>,
      Error,
      InfiniteData<PaginatedResponse<StructureDocumentType>>,
      ReturnType<typeof structureDocumentsQueryKeys.infiniteList>,
      PaginationParams
    >
  >[0],
) => {
  return useInfiniteQuery({
    queryKey: structureDocumentsQueryKeys.infiniteList(structureId, filter),
    queryFn: ({ pageParam }) =>
      getStructureDocuments(structureId, {
        ...filter,
        pagination: pageParam,
        sorting: filter?.sorting ?? [],
      }),
    getNextPageParam: (lastPage: PaginatedResponse<StructureDocumentType>) => {
      if (
        isNullish(lastPage.pageIndex) ||
        isNullish(lastPage.pageSize) ||
        lastPage.count <= (lastPage.pageIndex + 1) * lastPage.pageSize
      )
        return undefined;

      return {
        pageIndex: lastPage.pageIndex + 1,
        pageSize: lastPage.pageSize,
      };
    },
    initialPageParam: {
      pageIndex: 0,
      pageSize: 10,
    },
    enabled: structureId !== "",
    ...options,
  });
};
